// src/contexts/AuthContext.js
// ─────────────────────────────────────────────────────────────────────────────
// React context that:
//   1. Initialises the Telegram WebApp SDK (expands to full screen, etc.)
//   2. Calls GET /api/auth/me on mount to get the real user profile + balance
//   3. Exposes { user, balance, loading, error, refreshBalance } to any child
//
// Wrap your entire app with <AuthProvider> so every screen has access:
//
//   import { AuthProvider } from "./contexts/AuthContext";
//   <AuthProvider><App /></AuthProvider>
//
// Consume in a component:
//   import { useAuth } from "../contexts/AuthContext";
//   const { user, balance } = useAuth();
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { api } from "../services/api";
import socketService from "../services/socket";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [playBalance, setPlayBalance] = useState(0);
  const [mainBalance, setMainBalance] = useState(0);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // add near the other useState calls
  const [soundMuted, setSoundMuted] = useState(false);

  // ── Initialise Telegram Mini App SDK ────────────────────────────────────
  useEffect(() => {
    try {
      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.ready(); // tell Telegram the app is ready
        tg.expand(); // expand to full screen
      }
    } catch (_) {
      // Not running inside Telegram — dev browser mode, ignore
    }
  }, []);

  // ── Load user profile from backend ──────────────────────────────────────
  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMe();
      setUser(data.user);
      setSoundMuted(!!data.user.sound_muted);
      setBalance(parseFloat(data.user.balance ?? 0));
      setPlayBalance(parseFloat(data.user.playBalance ?? 0));
      setMainBalance(parseFloat(data.user.mainBalance ?? 0));

      // Open the WebSocket connection as soon as we have a verified user
      socketService.connect();
    } catch (err) {
      console.error("Auth error:", err.message);
      setError(err.message);

      // Graceful dev fallback — use a stub user so the UI renders
      if (process.env.NODE_ENV === "development") {
        setUser({
          id: 12345,
          first_name: "Dev",
          username: "devuser",
          balance: 500.0,
        });
        setPlayBalance(500.0);
        setMainBalance(0);
        socketService.connect();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // ── Re-fetch balance (call after a game ends or wallet action) ───────────
  const refreshBalance = useCallback(async () => {
    try {
      const data = await api.getWallet();
      setPlayBalance(data.playBalance);
      setMainBalance(data.mainBalance);
    } catch (err) {
      console.warn("Could not refresh balance:", err.message);
    }
  }, []);
  const refreshUser = useCallback(async () => {
    try {
      const data = await api.getMe();
      setUser(data.user);
      setPlayBalance(parseFloat(data.user.playBalance ?? 0));
      setMainBalance(parseFloat(data.user.mainBalance ?? 0));
    } catch (err) {
      console.warn("Could not refresh user:", err.message);
    }
  }, []);
  const toggleSound = useCallback(async () => {
    setSoundMuted((prev) => {
      const next = !prev;
      api.updateSoundSetting(next).catch(() => setSoundMuted(prev)); // revert on failure
      return next;
    });
  }, []);
  return (
    <AuthContext.Provider
      value={{
        user,
        balance: playBalance + mainBalance, // convenience for not-yet-updated UI
        loading,
        error,
        playBalance,
        mainBalance,
        refreshBalance,
        refreshUser,
        soundMuted,
        toggleSound,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
